// DesignToggle — switch between the Classic and Modern designs of the public
// pages (landing, sign-up, login). Colours come from the one theme, Harbor
// (the --rp-* tokens); the design picks layout, type and shape
// (data-design="modern", styled in design-modern.css). Modern is the default
// (2026-10-22); Classic is opt-in and is the one remembered choice, so a
// visitor who never touches the switch always gets the current default.
// Persists to localStorage.

import { Sparkles } from "lucide-react";
import { useState } from "react";

type DesignName = "classic" | "modern";

const STORAGE_KEY = "rp-design";

function savedDesign(): DesignName {
  return localStorage.getItem(STORAGE_KEY) === "classic" ? "classic" : "modern";
}

export function applyDesign(name: DesignName) {
  const el = document.documentElement;
  if (name === "classic") el.removeAttribute("data-design");
  else el.setAttribute("data-design", name);
  localStorage.setItem(STORAGE_KEY, name);
}

// Call once at app start (main.tsx), before the first render.
export function restoreDesign() {
  const el = document.documentElement;
  if (savedDesign() === "modern") el.setAttribute("data-design", "modern");
  else el.removeAttribute("data-design");
}

export function DesignToggle() {
  const [modern, setModern] = useState(savedDesign() === "modern");

  function toggle() {
    const next = !modern;
    applyDesign(next ? "modern" : "classic");
    setModern(next);
  }

  return (
    <button
      type="button"
      role="switch"
      aria-checked={modern}
      title={modern ? "Switch to the Classic design" : "Try the Modern design"}
      onClick={toggle}
      className="rp-design-toggle"
    >
      <Sparkles size={14} strokeWidth={2.2} aria-hidden />
      {/* Visually hidden on phones (index.css); still the accessible name. */}
      <span className="rp-design-toggle__label">Modern</span>
      <span className="rp-design-toggle__track" aria-hidden>
        <span className="rp-design-toggle__thumb" />
      </span>
    </button>
  );
}
